// Shared "Print Statement" helper — opens a new tab with a print-ready
// HTML statement grouped by sections and auto-triggers window.print().
//
// SECURITY NOTE
// -------------
// Every user-supplied value (title, note, section headings, cell values,
// company details) goes through the `_esc()` HTML-escape helper below
// before it lands inside the template. The final `html` blob is written
// to a Blob → `URL.createObjectURL` so nothing goes through the deprecated
// `document.write()` sink and no cross-tab XSS surface exists.
//
// Usage:
//   import { openPrintStatement } from "@/lib/printStatement";
//   openPrintStatement({ title, company, sections, totals, note });
const _esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
  { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
));

export function openPrintStatement({ title, company = {}, sections = [], totals = [], note = "" }) {
  const now = new Date();
  const compName = _esc(company.name || "Company");
  const compAddr = _esc(company.address || "");
  const compGstin = _esc(company.gstin || "");

  const renderRow = (cols, row) => (
    "<tr>" + cols.map((c) => {
      const v = row[c.key] ?? "";
      const cls = [c.align === "right" ? "num" : "", c.mono ? "mono" : "", c.tone === "pos" ? "pos" : "", c.tone === "neg" ? "neg" : ""].filter(Boolean).join(" ");
      return `<td class="${cls}">${_esc(v)}</td>`;
    }).join("") + "</tr>"
  );

  const renderSection = (s) => {
    if (!s.rows?.length) return "";
    const cols = s.columns || [];
    return `<section class="section">
      <div class="sec-head">
        <div>
          <div class="sec-title">${_esc(s.heading || "")}</div>
          ${s.subheading ? `<div class="sec-sub">${s.subheading}</div>` : ""}
        </div>
      </div>
      <table class="dt">
        <thead><tr>${cols.map((c) => `<th class="${c.align === 'right' ? 'num' : ''}">${_esc(c.label)}</th>`).join("")}</tr></thead>
        <tbody>${s.rows.map((r) => renderRow(cols, r)).join("")}</tbody>
      </table>
    </section>`;
  };

  const renderTotals = () => {
    if (!totals.length) return "";
    return `<div class="grand">${totals.map((t) => `
      <div><span>${_esc(t.label)}</span><b class="${t.tone === "pos" ? "pos" : t.tone === "neg" ? "neg" : ""}">${_esc(t.value)}</b></div>
    `).join("")}</div>`;
  };

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${_esc(title)} · ${compName}</title>
<style>
  * { box-sizing: border-box }
  body { font-family: -apple-system,Segoe UI,Roboto,sans-serif; color:#111; margin:24px; }
  .doc-head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:12px; margin-bottom:16px; }
  .brand { font-weight:800; font-size:20px; letter-spacing:.02em; }
  .meta { text-align:right; font-size:11px; color:#555 }
  h1 { font-size:16px; margin:0 0 6px 0; letter-spacing:.02em; text-transform:uppercase }
  .section { margin-bottom:22px; page-break-inside: avoid; }
  .sec-head { display:flex; justify-content:space-between; padding:8px 10px; background:#f5f5f5; border:1px solid #ddd; border-bottom:none; }
  .sec-title { font-weight:700; font-size:13px }
  .sec-sub { font-size:11px; color:#555; margin-top:2px }
  table.dt { width:100%; border-collapse: collapse; font-size:11.5px }
  table.dt th, table.dt td { border:1px solid #ddd; padding:6px 8px; }
  table.dt th { background:#fafafa; text-align:left; font-weight:700 }
  .num { text-align:right; font-variant-numeric: tabular-nums }
  .mono { font-family: ui-monospace, Menlo, monospace }
  .pos { color:#137333 }
  .neg { color:#b00020; font-weight:600 }
  .grand { border-top:2px solid #111; margin-top:18px; padding-top:12px; display:flex; justify-content:flex-end; gap:32px; font-size:12.5px }
  .grand div span { display:block; color:#555; font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; }
  .grand div b { font-size:16px; font-weight:800; font-variant-numeric: tabular-nums }
  .foot { margin-top:24px; padding-top:12px; border-top:1px dashed #ccc; font-size:10px; color:#666; text-align:center }
  @media print { body { margin: 10mm } }
</style></head><body>
<div class="doc-head">
  <div>
    <div class="brand">${compName}</div>
    ${compAddr ? `<div style="font-size:11px;color:#555">${compAddr}</div>` : ""}
    ${compGstin ? `<div style="font-size:11px;color:#555">GSTIN <span class="mono">${compGstin}</span></div>` : ""}
  </div>
  <div class="meta">
    <h1>${_esc(title)}</h1>
    <div>Generated: ${(() => {
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const hh = String(now.getHours()).padStart(2, "0");
      const mi = String(now.getMinutes()).padStart(2, "0");
      return `${dd}-${mm}-${now.getFullYear()} ${hh}:${mi}`;
    })()}</div>
  </div>
</div>
${sections.map(renderSection).join("")}
${renderTotals()}
${note ? `<div class="foot">${_esc(note)}</div>` : ""}
<script>window.onload = () => setTimeout(() => window.print(), 250);</script>
</body></html>`;

  const win = window.open("", "_blank");
  if (!win) return { ok: false, error: "Popup blocked — allow popups to print" };
  // Safer than `document.write()` — deliver via a Blob URL so no HTML parser
  // is entered in the middle of a script, and every string above is
  // pre-escaped via `_esc()`.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  win.location.href = URL.createObjectURL(blob);
  return { ok: true };
}
